import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { BookOpen, Crown, ExternalLink, Film, Headphones, Mic, Newspaper, Share2, Users, Trophy, Feather, Menu, X, TreePine, Copy, Smartphone, Clock, Link2, PenTool, BookMarked, Library, Mail, Vault, Home, Sparkles, Swords, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import SecretEntry from '@/components/SecretEntry';
import HamburgerMenu from '@/components/HamburgerMenu';
import SwordsIntro from '@/components/SwordsIntro';

// Small uppercase section divider for the hamburger menu (Personal / Community).
const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-2 px-2 mt-1 mb-1.5">
    <div className="h-px flex-1 bg-[#3D3531]" />
    <span className="text-[10px] uppercase tracking-[0.32em] text-[#8A817C]" style={{fontFamily: 'Work Sans, sans-serif'}}>{children}</span>
    <div className="h-px flex-1 bg-[#3D3531]" />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LMenuItem = ({ icon: Icon, label, onClick, testid, gold }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-2.5 py-2 rounded-lg transition-colors flex items-center gap-2 ${
      gold
        ? 'text-[#E8C5A0] hover:bg-[#D4AF37]/10 border border-transparent hover:border-[#D4AF37]/30'
        : 'text-[#F9F8F6] hover:bg-white/10'
    }`}
    data-testid={testid}
  >
    <Icon className={`w-3.5 h-3.5 shrink-0 ${gold ? 'text-[#D4AF37]' : 'text-[#E8C5A0]'}`} />
    <span className="text-[11px] sm:text-sm font-medium leading-snug" style={{fontFamily: 'Cormorant Garamond, serif'}}>{label}</span>
  </button>
);

// Reusable laurel-leaf section divider — guaranteed-symmetric via mirrored <use>.
// LaurelPill — a clickable pill where the laurel wreath itself frames the text.
// Replaces the previous "gold-bordered pill" treatment with a more reverent,
// hand-crafted Roman wreath that embraces the words from both sides.
const LaurelBranch = ({ flip = false }) => (
  <svg viewBox={flip ? "-6 -16 50 32" : "-44 -16 50 32"} className="h-10 sm:h-16 md:h-20 w-auto block flex-shrink-0" aria-hidden="true">
    <defs>
      <linearGradient id={flip ? 'pillLaurelR' : 'pillLaurelL'} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#D4AF37" />
        <stop offset="50%" stopColor="#E8C5A0" />
        <stop offset="100%" stopColor="#9F7B2C" />
      </linearGradient>
    </defs>
    <g transform={flip ? 'scale(-1 1)' : undefined}>
      <path d="M-5 0 Q-20 -10 -42 -12" fill="none" stroke={`url(#${flip ? 'pillLaurelR' : 'pillLaurelL'})`} strokeWidth="1.2" strokeLinecap="round" />
      <ellipse cx="-12" cy="-8"   rx="5"   ry="2"   transform="rotate(-30 -12 -8)"   fill={`url(#${flip ? 'pillLaurelR' : 'pillLaurelL'})`} stroke="#9F7B2C" strokeWidth="0.4" />
      <ellipse cx="-20" cy="-11"  rx="5"   ry="2"   transform="rotate(-25 -20 -11)"  fill={`url(#${flip ? 'pillLaurelR' : 'pillLaurelL'})`} stroke="#9F7B2C" strokeWidth="0.4" />
      <ellipse cx="-28" cy="-12"  rx="5"   ry="2"   transform="rotate(-15 -28 -12)"  fill={`url(#${flip ? 'pillLaurelR' : 'pillLaurelL'})`} stroke="#9F7B2C" strokeWidth="0.4" />
      <ellipse cx="-36" cy="-11.5" rx="4.5" ry="1.8" transform="rotate(-5 -36 -11.5)" fill={`url(#${flip ? 'pillLaurelR' : 'pillLaurelL'})`} stroke="#9F7B2C" strokeWidth="0.4" />
      <ellipse cx="-9"  cy="-2"   rx="4.5" ry="1.8" transform="rotate(-50 -9 -2)"    fill={`url(#${flip ? 'pillLaurelR' : 'pillLaurelL'})`} stroke="#9F7B2C" strokeWidth="0.4" />
      <ellipse cx="-16" cy="-4"   rx="4.5" ry="1.8" transform="rotate(-40 -16 -4)"   fill={`url(#${flip ? 'pillLaurelR' : 'pillLaurelL'})`} stroke="#9F7B2C" strokeWidth="0.4" />
      <ellipse cx="-24" cy="-6"   rx="4.5" ry="1.8" transform="rotate(-30 -24 -6)"   fill={`url(#${flip ? 'pillLaurelR' : 'pillLaurelL'})`} stroke="#9F7B2C" strokeWidth="0.4" />
      <ellipse cx="-32" cy="-7"   rx="4.5" ry="1.8" transform="rotate(-20 -32 -7)"   fill={`url(#${flip ? 'pillLaurelR' : 'pillLaurelL'})`} stroke="#9F7B2C" strokeWidth="0.4" />
    </g>
  </svg>
);

const LaurelPill = ({ onClick, children, testId, ariaLabel }) => (
  <div className="max-w-3xl mx-auto px-3 flex justify-center">
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label={ariaLabel}
      className="laurel-pill group inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-1.5 sm:py-2.5 transition-all duration-300 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/60 rounded-full max-w-full"
      style={{
        background: 'rgba(212,175,55,0.04)',
        border: '1px solid rgba(212,175,55,0.18)',
        boxShadow: '0 6px 22px rgba(0,0,0,0.45), 0 0 0 1px rgba(212,175,55,0.08), inset 0 1px 0 rgba(212,175,55,0.10)',
        cursor: 'pointer',
      }}
    >
      <LaurelBranch />
      <span
        className="italic tracking-[0.04em] sm:tracking-[0.08em] text-sm leading-snug sm:leading-normal sm:text-base md:text-lg text-center transition-colors group-hover:text-[#E8C5A0] break-words"
        style={{
          color: '#D4AF37',
          fontFamily: 'Cormorant Garamond, serif',
          textShadow: '0 1px 2px rgba(0,0,0,0.45), 0 0 18px rgba(212,175,55,0.18)',
        }}
      >
        {children}
      </span>
      <LaurelBranch flip />
    </button>
    <style>{`
      @keyframes laurelGlow {
        0%, 100% { box-shadow: 0 6px 22px rgba(0,0,0,0.45), 0 0 0 1px rgba(212,175,55,0.08), inset 0 1px 0 rgba(212,175,55,0.10); }
        50%      { box-shadow: 0 8px 28px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,175,55,0.22), 0 0 32px rgba(212,175,55,0.18), inset 0 1px 0 rgba(212,175,55,0.14); }
      }
      .laurel-pill { animation: laurelGlow 4.5s ease-in-out infinite; }
      .laurel-pill:hover { animation: none; box-shadow: 0 12px 34px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,175,55,0.45), 0 0 40px rgba(212,175,55,0.28), inset 0 1px 0 rgba(212,175,55,0.18) !important; border-color: rgba(212,175,55,0.5) !important; background: rgba(212,175,55,0.08) !important; }
      @media (prefers-reduced-motion: reduce) { .laurel-pill { animation: none; } }
    `}</style>
  </div>
);

const LaurelDivider = () => (
  <div className="max-w-3xl mx-auto px-6">
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-6">
      <span className="h-px w-full bg-gradient-to-r from-transparent via-[#D4AF37]/35 to-[#D4AF37]/55" />
      <svg viewBox="-60 -22 120 44" className="h-14 sm:h-16 w-auto block" aria-hidden="true">
        <defs>
          <linearGradient id="laurelGold" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#D4AF37" />
            <stop offset="50%" stopColor="#E8C5A0" />
            <stop offset="100%" stopColor="#9F7B2C" />
          </linearGradient>
          {/* One canonical branch, drawn pointing LEFT from origin (0,0).
              Path tip at x=-42, rooted at x=-5. Mirrored via scale(-1,1) for the right side. */}
          <g id="laurel-branch">
            <path d="M-5 0 Q-20 -10 -42 -12" fill="none" stroke="url(#laurelGold)" strokeWidth="1.2" strokeLinecap="round" />
            {/* Top row leaves (above branch) */}
            <ellipse cx="-12" cy="-8"  rx="5"   ry="2"   transform="rotate(-30 -12 -8)"  fill="url(#laurelGold)" stroke="#9F7B2C" strokeWidth="0.4" />
            <ellipse cx="-20" cy="-11" rx="5"   ry="2"   transform="rotate(-25 -20 -11)" fill="url(#laurelGold)" stroke="#9F7B2C" strokeWidth="0.4" />
            <ellipse cx="-28" cy="-12" rx="5"   ry="2"   transform="rotate(-15 -28 -12)" fill="url(#laurelGold)" stroke="#9F7B2C" strokeWidth="0.4" />
            <ellipse cx="-36" cy="-11.5" rx="4.5" ry="1.8" transform="rotate(-5 -36 -11.5)" fill="url(#laurelGold)" stroke="#9F7B2C" strokeWidth="0.4" />
            {/* Bottom row leaves (below branch) */}
            <ellipse cx="-9"  cy="-2"  rx="4.5" ry="1.8" transform="rotate(-50 -9 -2)"   fill="url(#laurelGold)" stroke="#9F7B2C" strokeWidth="0.4" />
            <ellipse cx="-16" cy="-4"  rx="4.5" ry="1.8" transform="rotate(-40 -16 -4)"  fill="url(#laurelGold)" stroke="#9F7B2C" strokeWidth="0.4" />
            <ellipse cx="-24" cy="-6"  rx="4.5" ry="1.8" transform="rotate(-30 -24 -6)"  fill="url(#laurelGold)" stroke="#9F7B2C" strokeWidth="0.4" />
            <ellipse cx="-32" cy="-7"  rx="4.5" ry="1.8" transform="rotate(-20 -32 -7)"  fill="url(#laurelGold)" stroke="#9F7B2C" strokeWidth="0.4" />
          </g>
        </defs>
        {/* Left branch — drawn directly */}
        <use href="#laurel-branch" />
        {/* Right branch — perfect mirror of the same shape */}
        <use href="#laurel-branch" transform="scale(-1 1)" />
        {/* Center medallion */}
        <circle cx="0" cy="0" r="3.5" fill="#D4AF37" />
        <circle cx="0" cy="0" r="1.7" fill="#1C1917" />
      </svg>
      <span className="h-px w-full bg-gradient-to-l from-transparent via-[#D4AF37]/35 to-[#D4AF37]/55" />
    </div>
  </div>
);

const Landing = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [featuredLinks, setFeaturedLinks] = useState([]);
  const [featuredVoices, setFeaturedVoices] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [coinToggles, setCoinToggles] = useState(null);
  // Inline audio player modal — opened when a visitor taps a Legacy Link with
  // an audio file. Previously these navigated the browser to the raw audio URL
  // which left mobile users on a black page with no way back.
  const [audioModal, setAudioModal] = useState(null);
  // Optional ?invite=token gate — admin-minted share links surface specific docs
  // (investor pitch / business plan / founder's vision) only to that recipient.
  const [inviteDocs, setInviteDocs] = useState({
    show_investor_pitch: false,
    show_business_plan: false,
    show_founders_vision: false,
    label: null,
    valid: false,
  });

  useEffect(() => {
    const fetchFeaturedLinks = async () => {
      try {
        const response = await axios.get(`${API}/links/featured?limit=6`);
        setFeaturedLinks(response.data);
      } catch (error) {
        console.error('Failed to fetch featured links:', error);
      }
    };
    fetchFeaturedLinks();
    // Featured voices (up to 2 — newest + most-appreciated)
    axios.get(`${API}/testimonials/featured`)
      .then(r => setFeaturedVoices(r.data?.items || []))
      .catch(() => {});
    // Check if user is logged in
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => {
        setCurrentUser(res.data);
        if (res.data?.is_admin) {
          axios.get(`${API}/admin/dashboard-coins`, { withCredentials: true })
            .then(r => setCoinToggles(r.data))
            .catch(() => {});
        }
      })
      .catch(() => {});
    // Resolve ?invite=token if present
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('invite');
      if (token) {
        // Persist for the post-auth welcome page (cleared after first view)
        try { localStorage.setItem('colosseum_invite_token', token); } catch { /* ignore */ }
        axios.get(`${API}/share-links/resolve/${token}`)
          .then(r => { if (r.data?.valid) setInviteDocs({ ...r.data }); })
          .catch(() => {});
      }
    } catch { /* ignore */ }
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const [showWebviewWarning, setShowWebviewWarning] = useState(false);

  const isEmbeddedWebview = () => {
    // Iframe context — Google blocks OAuth inside any framed window
    // (Emergent's chat preview iframe, embedded preview panes, etc.).
    try {
      if (window.top !== window.self) return true;
    } catch {
      return true;
    }
    const ua = navigator.userAgent || '';
    // Known app webviews
    if (/FBAN|FBAV|Instagram|Snapchat|TikTok|LinkedIn|Twitter|Line|WeChat|MicroMessenger|WhatsApp|GSA|Pinterest|Reddit/i.test(ua)) return true;
    // Gmail iOS / Google apps suite — these tokens appear in Gmail's in-app
    // browser even when "Safari" also appears in the UA (SFSafariViewController),
    // so we must catch them explicitly. Owen's Katee bug (Feb 18, 2026): she
    // tapped a link in Gmail iOS → Google blocked the OAuth handoff back to
    // the in-app browser → generic "Authentication failed" toast.
    if (/GMail|Gmail|GoogleApp|Mobile.*Gmail/i.test(ua)) return true;
    // iOS: has AppleWebKit but no "Safari" = embedded webview
    if (/iPhone|iPad|iPod/.test(ua) && /AppleWebKit/.test(ua) && !/Safari/.test(ua)) return true;
    // Android: "wv" flag indicates a webview
    if (/Android/.test(ua) && /wv/.test(ua)) return true;
    return false;
  };

  const handleLogin = () => {
    // Direct Google OAuth (Jun 2026) — replaced auth.emergentagent.com
    // redirect with our own /login page. The Login page handles webview
    // detection + the Google consent redirect itself.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
    // URLS, THIS BREAKS THE AUTH.
    navigate('/login', { state: { next: '/dashboard' } });
  };

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(window.location.origin).then(() => {
      toast.success('Link copied! Paste it in Safari or Chrome.');
    }).catch(() => {
      toast.info(`Copy this link: ${window.location.origin}`);
    });
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success(t('subscribe.success'));
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('subscribe.already_subscribed'));
      } else {
        toast.error(t('subscribe.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const typeIcons = { film: Film, music: Headphones, article: Newspaper, podcast: Mic, social: Share2, general: ExternalLink };
  const typeColors = {
    film: 'bg-purple-100 text-purple-700', music: 'bg-blue-100 text-blue-700',
    article: 'bg-green-100 text-green-700', podcast: 'bg-orange-100 text-orange-700',
    social: 'bg-pink-100 text-pink-700', general: 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="theme-fixed min-h-screen overflow-x-hidden bg-[#1C1917]">
      {/* `theme-fixed` keeps the landing page always Charcoal + gold,
          regardless of the signed-in user's chosen theme. Owen's Jun
          2026 brief: brand identity is non-negotiable on the public
          face of the app. */}
      {/* One-time cinematic swords-clash intro (first visit only, gated
          by localStorage). See SwordsIntro.jsx for the animation.
          ── 2026-07-06 Owen: PAUSED for Builders competition. Re-enable
          post-launch by uncommenting the line below. */}
      {/* <SwordsIntro /> */}
      {/* Audio Player Modal — inline playback for Legacy Link audio files so visitors
          never get stranded on a blank black audio-stage page. Tap-outside or X to close. */}
      {audioModal && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAudioModal(null)}>
          <div
            className="relative bg-[#2D2520] rounded-2xl max-w-md w-full p-6 shadow-2xl border border-[#D4AF37]/50"
            onClick={e => e.stopPropagation()}
            data-testid="audio-player-modal"
          >
            <button
              onClick={() => setAudioModal(null)}
              aria-label="Close audio player"
              data-testid="audio-modal-close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#1C1917] border border-[#3D3531] text-[#C4B8AE] hover:text-[#D4AF37] hover:border-[#D4AF37] flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 mb-4 pr-8">
              <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center shrink-0">
                <Headphones className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-medium text-[#F9F8F6] leading-tight truncate" style={{fontFamily: 'Cormorant Garamond, serif'}}>{audioModal.title}</h3>
                <p className="text-xs text-[#8A817C] truncate">{audioModal.credit_line || audioModal.author_name}</p>
              </div>
            </div>
            {audioModal.description && (
              <p className="text-sm text-[#C4B8AE] leading-relaxed mb-4 line-clamp-4">{audioModal.description}</p>
            )}
            <audio
              src={audioModal.audio_src}
              controls
              autoPlay
              preload="metadata"
              className="w-full"
              data-testid="audio-modal-player"
            >
              Your browser does not support the audio element.
            </audio>
            <button
              onClick={() => setAudioModal(null)}
              className="mt-4 w-full text-center text-sm text-[#8A817C] hover:text-[#D4AF37] transition-colors py-2"
              data-testid="audio-modal-back"
            >
              Back to the Colosseum
            </button>
          </div>
        </div>
      )}
      {/* Webview Warning Modal */}
      {showWebviewWarning && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowWebviewWarning(false)}>
          <div className="bg-[#F9F8F6] rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-[#D6D1C9]" onClick={e => e.stopPropagation()} data-testid="webview-warning-modal">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Smartphone className="w-7 h-7 text-[#D4AF37]" />
              </div>
            </div>
            <h3 className="text-2xl text-center font-medium mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              Open in Your Browser
            </h3>
            <p className="text-center text-[#57534E] text-sm mb-5 leading-relaxed" style={{fontFamily: 'Work Sans, sans-serif'}}>
              Google sign-in needs Safari or Chrome to work. Copy the link below and paste it in your browser.
            </p>
            <div className="bg-white rounded-lg border border-[#D6D1C9] p-3 flex items-center gap-2 mb-4">
              <span className="text-xs text-[#57534E] truncate flex-1" style={{fontFamily: 'Work Sans, sans-serif'}}>{window.location.origin}</span>
              <Button onClick={handleCopyLink} size="sm" className="bg-[#2A2522] hover:bg-[#1C1917] border border-[#D4AF37] text-[#D4AF37] shrink-0" data-testid="copy-link-btn">
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
            </div>
            <button onClick={() => setShowWebviewWarning(false)} className="w-full text-center text-sm text-[#57534E] hover:text-[#D4AF37] transition-colors py-2" data-testid="webview-dismiss-btn">
              Dismiss
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1C1917]/95 backdrop-blur-sm border-b border-[#3D3531]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex justify-between items-center gap-3">
          {/* Brand: full wordmark "Welcome to the Colosseum™" on sm+,
              crossed swords + short "Colosseum Legacy" lockup on mobile
              (Owen's Jun 2026 brief — reclaim header width while keeping
              the swords-as-brand cue consistent with the inline menu). */}
          <button
            type="button"
            onClick={() => navigate('/')}
            data-testid="home-logo-link"
            className="flex items-center gap-2 min-w-0 cursor-pointer text-[#F9F8F6] hover:text-[#E8C5A0] transition-colors focus:outline-none"
            aria-label="Return home"
          >
            <Swords className="sm:hidden w-5 h-5 text-[#D4AF37] shrink-0" />
            <span className="sm:hidden text-base font-light tracking-tight truncate" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              Colosseum Legacy
            </span>
            <h1 className="hidden sm:block text-lg lg:text-xl font-light tracking-tighter whitespace-nowrap" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              Welcome to the Colosseum&#8482;
            </h1>
          </button>
          {/* Right side: Join/Dashboard + Hamburger (universal) */}
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher className="hidden sm:flex" />
            {currentUser ? (
              <>
                {/* Owen Feb 28, 2026 round 62 — Reading Room icon-button
                    is ADMIN-ONLY (Owen/wife admin accounts). This is the
                    administrative entry-point to the public Reading Room
                    he shares with investors/partners; non-admin users
                    don't need a top-bar pill for it on the landing page.
                    Visibility rule re-established after the round 61.x
                    over-removal. */}
                {currentUser?.is_admin && (
                  <button
                    type="button"
                    onClick={() => navigate('/reading-room')}
                    data-testid="reading-room-icon-btn"
                    aria-label="Open Reading Room"
                    title="Reading Room (admin)"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-[#D4AF37] bg-[#2A2522] text-[#D4AF37] hover:bg-[#1C1917] hover:text-[#E8C5A0] transition-colors"
                  >
                    <ScrollText className="w-4 h-4" />
                  </button>
                )}
                <Button onClick={() => navigate('/dashboard')} data-testid="dashboard-btn" className="bg-[#2A2522] hover:bg-[#1C1917] border border-[#D4AF37] text-[#D4AF37] text-sm px-3 h-8 hover:text-[#E8C5A0] whitespace-nowrap">
                  Dashboard
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-1 sm:gap-1.5">
                <Button onClick={handleLogin} data-testid="join-btn" className="bg-[#2A2522] hover:bg-[#1C1917] border border-[#D4AF37] text-[#D4AF37] text-xs sm:text-sm px-2 sm:px-3 h-8 hover:text-[#E8C5A0]">
                  {t('nav.join')}
                </Button>
                {/* "Or · Battle On" is the long-form CTA. Battle On was
                    hidden on mobile (Feb 24, 2026) when iPhone 16 viewport
                    overflowed — Owen flagged Jun 2026 that he wants it back.
                    Bringing it back across all breakpoints with tighter
                    padding + smaller text on mobile so the header fits.
                    Decorative "Or" stays sm+ only to save the 18px on mobile. */}
                <span className="hidden sm:inline text-[10px] text-[#8A817C] italic" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('nav.or')}</span>
                <Button onClick={handleLogin} variant="outline" data-testid="login-btn" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#E8C5A0] text-xs sm:text-sm px-2 sm:px-3 h-8 whitespace-nowrap">
                  {t('nav.battle_on')}
                </Button>
              </div>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 ml-1 text-[#C4B8AE] hover:text-[#E8C5A0] hover:bg-white/5 rounded-md transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Access All Areas"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {/* Slide-down menu (universal — laptop, tablet & mobile) */}
        {mobileMenuOpen && (
          <HamburgerMenu
            currentUser={currentUser}
            onClose={() => setMobileMenuOpen(false)}
            onTroubleSigningIn={() => setShowWebviewWarning(true)}
          />
        )}
      </header>

      {/* Invitation banner removed per founder direction (Feb 2026) — landing
          page no longer surfaces the Business Overview & Opportunities card
          when ?invite=token is present. The invite token is still resolved and
          persisted to localStorage so AuthCallback can route invited users to
          /founder-welcome after they sign in. */}

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Owen Feb 2026 speed pass — hero was a single 6 MB JPEG loaded
            as a CSS background. Replaced with a responsive `<picture>`
            using Pexels' native WebP + width transformations. Payload:
            phones 146 KB (was 6 MB → -97.5%), tablets 480 KB, desktop
            1.25 MB (-79%). Marked `fetchpriority="high"` because this
            IS the LCP element on `/`. `loading="eager"` because it's
            above-the-fold — deferring it kills the paint. `<picture>`
            absolutely positioned so the surrounding hero flexbox layout
            is untouched. */}
        <picture>
          <source
            type="image/webp"
            media="(max-width: 640px)"
            srcSet="https://images.pexels.com/photos/33562162/pexels-photo-33562162.jpeg?auto=compress&cs=tinysrgb&w=640&fm=webp"
          />
          <source
            type="image/webp"
            media="(max-width: 1280px)"
            srcSet="https://images.pexels.com/photos/33562162/pexels-photo-33562162.jpeg?auto=compress&cs=tinysrgb&w=1280&fm=webp"
          />
          <source
            type="image/webp"
            srcSet="https://images.pexels.com/photos/33562162/pexels-photo-33562162.jpeg?auto=compress&cs=tinysrgb&w=1920&fm=webp"
          />
          <img
            src="https://images.pexels.com/photos/33562162/pexels-photo-33562162.jpeg?auto=compress&cs=tinysrgb&w=1280"
            alt=""
            aria-hidden="true"
            fetchpriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover z-0"
            data-testid="hero-bg-image"
          />
        </picture>
        <div className="absolute inset-0 bg-black/40 z-0"></div>

        {/* Legend Original medallion — REMOVED (Feb 2026 pre-competition
            pass). Owen: "we need to remove that ASAP." Was a top-right
            prestige card advertising the $1,000 lifetime tier. Kept the
            legend page + hamburger entry intact — this was purely the
            landing-page hero card. */}


        {/* Take the Tour floating pill — REMOVED (Feb 2026). Was admin-only,
            floated at top-6 right-4 inside the hero, but sat behind the
            sticky header on mobile so only a sliver showed — read as an
            orphan flash to users on first paint. The hero CTA cluster now
            has a "Watch the tour →" link visible to everyone, and the
            dashboard has its own pill row. No longer needed here. */}

        <div className="relative z-10 text-center px-4 max-w-4xl">
          <h2 className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none font-light text-white mb-3" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Welcome to the Colosseum
          </h2>
          <p className="text-2xl sm:text-3xl text-[#D4AF37] italic tracking-wide mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            {t('landing_extras.hero_subtitle')}
          </p>
          <h3 className="text-2xl sm:text-3xl text-white/95 font-light mb-8" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            {t('hero.title')}
          </h3>

          {/* Hero CTA cluster — Begin your legacy + Copy link.
              "Copy link" sits immediately next to Join so anyone whose
              Google sign-in fails (typically when the site opens inside an
              app's webview) has a one-tap rescue path: paste into Chrome /
              Safari. Owen's Feb 2026 brief: "we need a copy link pill that
              is either first up or an immediate second." */}
          {!currentUser && (
            <div className="flex flex-wrap items-center justify-center gap-3" data-testid="hero-cta-cluster">
              <Button
                onClick={handleLogin}
                data-testid="hero-join-btn"
                className="px-6 sm:px-8 py-5 rounded-full shadow-md"
                style={{
                  background: '#D4AF37', color: '#1F1B19',
                  fontFamily: 'Cormorant Garamond, serif',
                  fontStyle: 'italic', letterSpacing: '0.04em', fontSize: '1.1rem',
                  border: 'none',
                }}
              >
                {t('landing_extras.begin_your_legacy')}
              </Button>
              <Button
                onClick={handleCopyLink}
                data-testid="hero-copy-link-btn"
                variant="outline"
                className="px-5 sm:px-7 py-5 rounded-full"
                style={{
                  border: '1.5px solid #D4AF37',
                  color: '#D4AF37',
                  background: 'rgba(28,25,23,0.5)',
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  fontFamily: 'Cormorant Garamond, serif',
                  fontStyle: 'italic', letterSpacing: '0.04em', fontSize: '1rem',
                }}
              >
                <Copy className="w-4 h-4 mr-2" /> {t('landing_extras.copy_link')}
              </Button>
            </div>
          )}
          {!currentUser && (
            <p
              className="mt-3 text-xs sm:text-sm italic text-[#E8DCC8]/85 max-w-md mx-auto leading-relaxed"
              data-testid="hero-copy-link-hint"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t('landing_extras.signin_wont_open')}
            </p>
          )}
          {/* "Watch the tour →" — quiet italic link under the CTAs.
              Owen's Feb 2026 brief: give visitors a no-commitment way to see
              the Colosseum before they sign in. */}
          <button
            onClick={() => navigate('/tour')}
            data-testid="hero-watch-tour-link"
            className="mt-4 inline-flex items-center gap-1 italic text-sm sm:text-base text-[#D4AF37] hover:text-[#E8C5A0] hover:bg-[#1C1917] transition-colors rounded-full border border-[#D4AF37] bg-[#1C1917]/85 backdrop-blur-sm px-4 py-1.5 shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('landing_extras.watch_the_tour')}
          </button>
        </div>

        {/* July Founding Special ribbon removed per Owen (Feb 2026).
            The /july-special page is still reachable directly; only
            the landing-page laurel pill has been retired. */}
      </section>

      {/* Tasteful gold laurel-wreath pill — bridges the Hero into "Your Battles Matter".
          The laurels themselves frame the clickable text, so the pill is the wreath. */}
      <div className="relative bg-[#1C1917] pt-16 pb-10">
        <LaurelPill
          onClick={() => {
            const lang = (i18n.language || 'en').split('-')[0];
            const suffix = lang && lang !== 'en' ? `?lang=${lang}` : '';
            window.location.href = `${BACKEND_URL}/api/view/why-built-for-you${suffix}`;
          }}
          testId="why-built-pill"
          ariaLabel={t('landing_extras.why_colosseum_built')}
        >
          {t('landing_extras.why_colosseum_built')}
        </LaurelPill>
      </div>

      {/* Your Battles Matter — moved up and restyled in parchment-scroll treatment to mirror Daily Journal */}
      <section className="pt-6 pb-20 sm:pb-24 px-4 bg-[#1C1917]" data-testid="submit-preview-section">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-xl overflow-hidden relative" style={{background: 'linear-gradient(135deg, #F5EDE0 0%, #EDE4D3 50%, #E8DCC8 100%)', border: '2px solid #C9A84C'}}>
            {/* Centurion medallion top-left (mirrors Daily Journal aesthetic) */}
            <div className="absolute top-6 left-6 w-12 h-12 rounded-full border-2 border-[#C9A84C] flex items-center justify-center bg-[#1C1917]/10" aria-hidden="true">
              <Feather className="w-5 h-5 text-[#9F7B2C]" style={{transform: 'rotate(-30deg)'}} />
            </div>
            <div className="p-8 sm:p-12 text-center">
              <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#9F7B2C] mb-6" style={{fontFamily: 'Work Sans, sans-serif'}}>
                {t('landing_extras.your_battles_matter')}
              </p>
              <h3 className="text-3xl sm:text-4xl text-[#1C1917] mb-5 font-medium tracking-tight" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('battles.title')}
              </h3>
              <p className="text-base sm:text-lg text-[#5A4A3A] mb-0 leading-relaxed max-w-xl mx-auto" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('battles.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Laurel separator */}
      <div className="bg-[#1C1917] py-10"><LaurelDivider /></div>

      {/* Daily Journal — clickable scenic image, sized to match the Your Battles Matter banner above. */}
      <section className="bg-[#1C1917] py-6 sm:py-10 px-3 sm:px-6" data-testid="daily-journal-section">
        <div className="max-w-3xl mx-auto">
          <button
            type="button"
            onClick={() => currentUser ? navigate('/journal') : handleLogin()}
            data-testid="journal-quill-cta"
            className="group relative block w-full h-56 sm:h-64 overflow-hidden rounded-2xl border border-[#D4AF37]/55 hover:border-[#D4AF37] shadow-[0_10px_40px_rgba(0,0,0,0.5)] transition-all duration-500 hover:shadow-[0_14px_50px_rgba(212,175,55,0.28)] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/70"
          >
            <img
              src="/images/journal_senator_dusk.jpg"
              alt="A senator pauses at a candlelit desk, journal open, quill in inkwell, Tuscan dusk through the window"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.025]"
              style={{filter: 'brightness(1.35) contrast(1.03) saturate(1.05)', objectPosition: 'center 40%'}}
            />
            {/* Soft top gradient — only enough to seat the eyebrow text against the dusk sky */}
            <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" />
            {/* Soft bottom gradient — only enough to seat the tagline against the table edge */}
            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

            {/* Eyebrow — top */}
            <p
              className="absolute top-4 sm:top-5 left-1/2 -translate-x-1/2 text-[10px] sm:text-xs uppercase tracking-[0.4em] text-[#D4AF37] drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)] pointer-events-none"
              style={{fontFamily: 'Work Sans, sans-serif'}}
            >
              {t('landing_journal.eyebrow')}
            </p>

            {/* Tagline — bottom */}
            <p
              className="absolute bottom-4 sm:bottom-5 left-1/2 -translate-x-1/2 w-[90%] text-center text-sm sm:text-base italic text-[#F4EFE6] drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)] pointer-events-none"
              style={{fontFamily: 'Cormorant Garamond, serif'}}
              dangerouslySetInnerHTML={{__html: t('landing_journal.tagline')}}
            />
          </button>
        </div>
      </section>

      {/* Features Section */}

      {/* Laurel separator */}
      <div className="bg-[#1C1917] py-10"><LaurelDivider /></div>

      <section className="py-20 px-4" style={{background: 'linear-gradient(135deg, #F5EDE0 0%, #EDE4D3 50%, #E8DCC8 100%)'}} data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl sm:text-4xl text-center mb-4 font-medium tracking-tight text-[#1C1917]" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            {t('features.title')}
          </h3>
          <p className="text-center text-base text-[#5A4A3A] mb-14 max-w-2xl mx-auto leading-relaxed" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            {t('landing_extras.three_pillars')}
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* SIGNATURE — Family Legacy Scroll (lady & guard scene) */}
            <button
              onClick={() => currentUser ? navigate(`/family-tree/${currentUser.user_id}`) : handleLogin()}
              data-testid="feature-family-scroll"
              className="pillar-card pillar-card--signature group relative h-[420px] rounded-xl overflow-hidden text-left border border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-2"
            >
              <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: `url(${BACKEND_URL}/api/uploads/static/family_legacy_scroll_card.jpg)`}} aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" aria-hidden="true" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2A2522] text-[#D4AF37] border border-[#D4AF37] z-10 shadow-lg" style={{fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '0.78rem', letterSpacing: '0.1em'}}>
                <Feather className="w-3 h-3" />
                {t('landing_extras.signature_pill')}
              </div>
              <div className="relative z-10 h-full flex flex-col justify-end p-6">
                <Users className="w-9 h-9 text-[#D4AF37] mb-3 drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)]" />
                <h4 className="text-2xl font-medium text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('hmenu.family_legacy_scroll')}</h4>
              </div>
            </button>

            {/* Tell Your Stories (scribe by candlelight) */}
            <button
              onClick={() => currentUser ? navigate('/authors-hub') : handleLogin()}
              data-testid="feature-submit"
              className="pillar-card group relative h-[420px] rounded-xl overflow-hidden text-left border border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-2"
            >
              <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: `url(${BACKEND_URL}/api/uploads/static/daily_journal_card.jpg)`}} aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" aria-hidden="true" />
              <div className="relative z-10 h-full flex flex-col justify-end p-6">
                <BookOpen className="w-9 h-9 text-[#D4AF37] mb-3 drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)]" />
                <h4 className="text-2xl font-medium text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('landing_extras.tell_your_stories')}</h4>
              </div>
            </button>

            {/* Highly Appreciated (elder reading at the Colosseum) */}
            <button
              onClick={() => currentUser ? navigate('/highly-appreciated') : handleLogin()}
              data-testid="feature-appreciated"
              className="pillar-card group relative h-[420px] rounded-xl overflow-hidden text-left border border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-2"
            >
              <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: `url(${BACKEND_URL}/api/uploads/static/highly_appreciated_card.jpg)`}} aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" aria-hidden="true" />
              <div className="relative z-10 h-full flex flex-col justify-end p-6">
                <Trophy className="w-9 h-9 text-[#D4AF37] mb-3 drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)]" />
                <h4 className="text-2xl font-medium text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('landing_extras.highly_appreciated')}</h4>
              </div>
            </button>
          </div>
          {/* Lifted-card aesthetic — same family as the dashboard tiles. A soft black drop
              shadow gives depth, a faint inner gold rim catches light, and a slight resting
              translate-Y makes them feel suspended above the page rather than flush with it. */}
          <style>{`
            .pillar-card {
              transform: translateY(-2px);
              box-shadow:
                0 18px 36px -12px rgba(0,0,0,0.65),
                0 8px 16px -8px rgba(0,0,0,0.55),
                inset 0 1px 0 rgba(212,175,55,0.10);
            }
            .pillar-card:hover {
              box-shadow:
                0 28px 50px -14px rgba(0,0,0,0.75),
                0 14px 24px -10px rgba(0,0,0,0.6),
                0 0 0 1px rgba(212,175,55,0.30),
                0 0 36px rgba(212,175,55,0.18),
                inset 0 1px 0 rgba(212,175,55,0.18);
            }
            .pillar-card--signature {
              box-shadow:
                0 22px 44px -12px rgba(0,0,0,0.7),
                0 10px 20px -8px rgba(0,0,0,0.55),
                0 0 0 1px rgba(212,175,55,0.18),
                inset 0 1px 0 rgba(212,175,55,0.16);
            }
            .pillar-card--signature:hover {
              box-shadow:
                0 32px 60px -14px rgba(0,0,0,0.78),
                0 16px 28px -10px rgba(0,0,0,0.62),
                0 0 0 1px rgba(212,175,55,0.5),
                0 0 48px rgba(212,175,55,0.28),
                inset 0 1px 0 rgba(212,175,55,0.22);
            }
            @media (prefers-reduced-motion: reduce) {
              .pillar-card { transition: none; }
            }
          `}</style>
        </div>
      </section>

      {/* Laurel separator (parchment → dark) */}
      <div className="bg-[#1C1917] py-10"><LaurelDivider /></div>

      {/* Thousand Word Canvas Spotlight */}
      <section className="py-24 px-4 relative overflow-hidden" data-testid="canvas-spotlight">
        {/* Scroll background image */}
        <div className="absolute inset-0" style={{backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/56f84a5b-135a-42ae-a69d-86265ff2961c/images/e5de931b3090f5ea40bb6d6d9638be33cdac4ead1c8c7d94a43c79b0b1558ee5.png)', backgroundSize: 'cover', backgroundPosition: 'center'}} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/40" />
        {/* Roman guard watermark */}
        <img src="https://static.prod-images.emergentagent.com/jobs/56f84a5b-135a-42ae-a69d-86265ff2961c/images/fa44e5bee66a04a1c9cfb60052c107ea37018590607b1954060010a7e7f35dbc.png" alt="" className="absolute right-8 top-1/2 -translate-y-1/2 h-[60%] object-contain opacity-[0.05] pointer-events-none select-none" aria-hidden="true" />

        {/* Anthem parchment background text */}
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none select-none" aria-hidden="true">
          <div className="max-w-4xl w-full px-8 text-center opacity-[0.06]" style={{fontFamily: "'Homemade Apple', 'Dancing Script', cursive", fontSize: 'clamp(0.65rem, 1.2vw, 0.85rem)', lineHeight: '2.2', color: '#C9A84C'}}>
            <p>I will sing to you an anthem I will sing to you a love song I will sing to you all the days of your life until the sun and the moon are gone I will sing this song into your lives to the heart of broken truth tonight sealed by the spirit it will never leave it's a song that'll bring your hearts back to believe in love</p>
            <p className="mt-4">And I need your Love And I need your Love I need I need I need I need your love I need your Love</p>
            <p className="mt-4">I will rock you with this anthem I will rock with you in this love song I will rock with you all the days of your life until the sun and the moon are gone I will take a million moonlight walks under the shadows of the trees and I will fall upon the starry nights and I will scream at you from my knees</p>
          </div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{background: '#2A2522', color: '#D4AF37', border: '1px solid #D4AF37', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '0.8rem', letterSpacing: '0.1em'}}>
                <Feather className="w-3 h-3" />
                {t('landing_extras.signature_pill')}
              </div>
              <h3 className="text-4xl sm:text-5xl text-white mb-3 font-medium tracking-tight leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]" style={{fontFamily: 'Cormorant Garamond, serif'}} data-testid="canvas-heading">
                {t('landing_extras.thousand_word_canvas')}&#8482;
              </h3>
              <p className="text-base text-white/60 italic leading-relaxed mb-3 drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('landing_extras.your_artistic_expressions')}
              </p>
              <p className="text-xl text-white/80 italic leading-relaxed mb-8 drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]" style={{fontFamily: 'Cormorant Garamond, serif'}} data-testid="canvas-tagline">
                {t('landing_extras.if_picture_paints')}
              </p>
              <Button
                onClick={currentUser ? () => navigate('/canvas') : handleLogin}
                data-testid="canvas-cta-btn"
                className="bg-[#2A2522] hover:bg-[#1C1917] border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-sm rounded-full hover:text-[#E8C5A0] group shadow-lg"
              >
                {t('landing_extras.start_your_canvas')}
                <Feather className="ml-2 w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="hidden md:flex justify-center">
              {/* Picture frame */}
              <div className="relative p-3 bg-gradient-to-br from-[#8B7355] via-[#A0885C] to-[#6B5B3F] rounded shadow-[0_8px_32px_rgba(0,0,0,0.5)] inline-block">
                <div className="absolute inset-[3px] border border-[#C4A964]/30 rounded pointer-events-none"></div>
                <div className="absolute inset-[6px] border border-[#8B7355]/40 rounded pointer-events-none"></div>
                <div className="bg-[#2A2522] border border-[#D4AF37]/20 rounded px-7 py-6 shadow-inner">
                  <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4" style={{fontFamily: 'Work Sans, sans-serif'}}>{t('landing_extras.thousand_word_canvas')}</div>
                  <div
                    className="space-y-1 text-white/70 italic leading-relaxed prose-poem"
                    style={{fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem'}}
                    dangerouslySetInnerHTML={{__html: t('landing_extras.thousand_word_poem')}}
                  />
                  <div className="mt-4 pt-3 border-t border-[#D4AF37]/20 text-right">
                    <span className="text-[#D4AF37] text-sm">&mdash; {t('landing_extras.thousand_word_poem_attrib')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Legacy Links — moved up so Time Capsules becomes the emotional climax */}
      {featuredLinks.length > 0 && (
        <section className="py-20 px-4 bg-[#1C1917]" data-testid="featured-links-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3" style={{fontFamily: 'Work Sans, sans-serif'}}>
                {t('landing_extras.legacy_links_pill')}
              </p>
              <h3 className="text-3xl sm:text-4xl mb-4 font-medium tracking-tight text-[#F9F8F6]" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('landing_extras.your_links_voice_art')}
              </h3>
              <p className="text-[#C4B8AE] max-w-2xl mx-auto leading-relaxed" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('landing_extras.artistic_paragraph')}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {featuredLinks.map((link) => {
                const IconComponent = typeIcons[link.link_type] || ExternalLink;
                const colorClass = typeColors[link.link_type] || typeColors.general;
                // Uploaded audio links live at /api/uploads/audio/...; everything else uses link.url.
                // Audio links are handled inline via a modal player — NEVER navigate to the raw file
                // URL, because mobile webviews treat that as a page load and strand the user on a
                // black audio stage with no way back (the bug Owen reported).
                const hasAudio = link.link_type === 'audio' && link.audio_url;
                const externalHref = link.url && link.url.trim() ? link.url : null;
                const isAudioOnly = hasAudio && !externalHref;
                const ctaLabel = externalHref
                  ? t('archives.view')
                  : (hasAudio ? t('landing_extras.listen') : t('landing_extras.open_on_platform'));
                const handleClick = (e) => {
                  if (isAudioOnly) {
                    e.preventDefault();
                    setAudioModal({
                      title: link.title,
                      author_name: link.author_name,
                      credit_line: link.credit_line,
                      description: link.description,
                      audio_src: `${BACKEND_URL}${link.audio_url}`,
                    });
                    return;
                  }
                  if (!externalHref) {
                    e.preventDefault();
                    // No URL at all → route to contextual landing for more info
                    navigate(currentUser ? '/links' : '/discover-binding');
                  }
                };
                return (
                  <a
                    key={link.link_id}
                    href={externalHref || '#'}
                    target={externalHref ? '_blank' : undefined}
                    rel={externalHref ? 'noopener noreferrer' : undefined}
                    onClick={handleClick}
                    className="block group"
                    data-testid={`featured-link-${link.link_id}`}
                  >
                    <Card className="p-6 border border-[#3D3531] bg-[#2D2520] hover:-translate-y-1 transition-all duration-300 h-full">
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-lg font-medium mb-1 text-[#F9F8F6] group-hover:text-[#E8C5A0] transition-colors" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                            {link.title}
                          </h4>
                          <p className="text-xs text-[#8A817C]">{link.credit_line || `${t('common.by')} ${link.author_name}`}</p>
                        </div>
                      </div>
                      <p className="text-[#8A817C] text-sm leading-relaxed line-clamp-3 mb-4">{link.description}</p>
                      <div className="flex items-center gap-1 text-[#D4AF37] text-sm font-medium group-hover:gap-2 transition-all">
                        <ExternalLink className="w-4 h-4" />
                        {ctaLabel}
                      </div>
                    </Card>
                  </a>
                );
              })}
            </div>

            {/* CTA — visitors land in /library-hub (the cozy reading-and-vinyl page).
                Logged-out users hit ProtectedRoute, which prompts sign-in first. */}
            <div className="text-center mt-12">
              <button
                onClick={() => navigate('/library-hub')}
                data-testid="landing-library-cta"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-[#D4AF37] bg-[#2A2522] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300 text-sm uppercase tracking-[0.25em]"
                style={{fontFamily: 'Work Sans, sans-serif'}}
              >
                Visit the Colosseum Library
                <span className="text-lg leading-none">&rarr;</span>
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Laurel separator */}
      <div className="bg-[#1C1917] py-10"><LaurelDivider /></div>

      {/* Time Capsules — LIVE. Positioned as the emotional climax just before the Premium CTA. */}
      <section className="py-20 px-4 bg-[#1C1917]" data-testid="time-capsules-section">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-xl overflow-hidden" style={{background: 'linear-gradient(135deg, #F5EDE0 0%, #EDE4D3 50%, #E8DCC8 100%)', border: '2px solid #C9A84C'}}>
            <div className="p-8 sm:p-12 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-[0.15em] mb-6" style={{background: '#2A2522', color: '#D4AF37', border: '1px solid #D4AF37'}}>
                <Clock className="w-3.5 h-3.5" />
                Now Live
              </div>
              <h3 className="text-3xl sm:text-4xl text-[#1C1917] mb-4 font-medium tracking-tight" style={{fontFamily: 'Cormorant Garamond, serif'}} data-testid="time-capsules-heading">
                {t('hmenu.time_capsules')}
              </h3>
              <p className="text-base text-[#57534E] leading-relaxed max-w-2xl mx-auto mb-6" style={{fontFamily: 'Work Sans, sans-serif'}}>
                {t('landing_extras.time_capsule_paragraph')}
              </p>
              <p className="text-sm text-[#8A817C] italic" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('landing_extras.best_stories_quote')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voices of the Colosseum — what creators are saying */}
      {featuredVoices.length > 0 && (
        <section className="py-16 sm:py-20 px-4 bg-[#1C1917]" data-testid="landing-voices-section">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.18em] mb-5" style={{background: '#2A2522', color: '#D4AF37', border: '1px solid #D4AF37'}}>
                <Crown className="w-3.5 h-3.5" />
                {t('landing_extras.voices_of_colosseum')}
              </div>
              <h3 className="text-3xl sm:text-4xl text-[#F5EBD8] font-light tracking-tight" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('landing_extras.what_creators_saying')}
              </h3>
            </div>

            <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
              {featuredVoices.map(v => (
                <article
                  key={v.testimonial_id}
                  data-testid={`landing-voice-${v.testimonial_id}`}
                  className="rounded-2xl bg-gradient-to-br from-[#2D2520] to-[#1F1B19] border border-[#D4AF37]/30 p-6 sm:p-7 cursor-pointer hover:border-[#D4AF37]/60 transition-colors"
                  onClick={() => navigate(`/voices?t=${v.testimonial_id}`)}
                >
                  <p
                    className="text-[#E8DCC8] text-lg sm:text-xl leading-relaxed line-clamp-6"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    “{v.body}”
                  </p>
                  <div className="mt-5 flex items-center gap-2">
                    <span className="text-sm text-[#D4AF37] tracking-wide">— {v.author_first_name || v.author_name}</span>
                    {(v.is_premium || v.is_founder) && (
                      <img src="/gold-crown.png" alt="Premium" className="w-3.5 h-3.5" style={{ filter: 'drop-shadow(0 0 3px rgba(212,175,55,0.6))' }} />
                    )}
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button
                onClick={() => navigate('/voices')}
                variant="outline"
                data-testid="landing-voices-all-btn"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#E8C5A0]"
              >
                {t('landing_extras.read_more_voices')}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Laurel separator */}
      <div className="bg-[#1C1917] py-10"><LaurelDivider /></div>

      {/* Premium CTA — text on left, framed picture of the patriarch blessing the child on the right */}
      <section className="py-20 sm:py-24 px-4 bg-[#2D2520] border-y border-[#3D3531]" data-testid="premium-cta-section">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Text column */}
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-[0.15em] mb-6" style={{background: '#2A2522', color: '#D4AF37', border: '1px solid #D4AF37'}}>
              <Crown className="w-4 h-4" />
              {t('premium_cta.badge')}
            </div>
            <h3 className="text-3xl sm:text-4xl mb-4 font-medium tracking-tight text-[#F9F8F6]" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('premium_cta.title')}
            </h3>
            <p className="text-[#C4B8AE] leading-relaxed mb-8" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('premium_cta.subtitle')}
            </p>
            <Button
              onClick={() => navigate('/premium')}
              className="bg-[#2A2522] hover:bg-[#1C1917] border border-[#D4AF37] text-[#D4AF37] px-8 py-6 text-lg rounded-full hover:text-[#E8C5A0]"
              data-testid="premium-cta-btn"
            >
              {t('premium_cta.cta')}
            </Button>
          </div>

          {/* Framed image column */}
          <div className="relative aspect-[3/4] sm:aspect-[4/5] max-w-sm mx-auto md:max-w-none w-full rounded-xl overflow-hidden border-2 border-[#D4AF37]/40 shadow-2xl">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${BACKEND_URL}/api/uploads/static/premium_laurel_blessing.jpg?v=3)`,
                backgroundPosition: '70% 40%',
              }}
              aria-hidden="true"
            />
            {/* Warm vignette — radial gradient from transparent center to subtle dark edges,
                gives a gallery-print feel pairing with the gold inner border below. */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(28,25,23,0.35) 100%)'}}
              aria-hidden="true"
            />
            {/* Subtle gold inner frame */}
            <div className="absolute inset-2 rounded-lg border border-[#D4AF37]/25 pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Email Subscription */}
      <section className="py-20 px-4 bg-[#1C1917]" data-testid="subscribe-section">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-3xl sm:text-4xl mb-6 font-medium tracking-tight text-[#F9F8F6]" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            {t('subscribe.title')}
          </h3>
          <p className="text-[#8A817C] mb-8 leading-relaxed">{t('subscribe.subtitle')}</p>
          <form onSubmit={handleSubscribe} className="flex gap-3 max-w-md mx-auto" data-testid="subscribe-form">
            <Input
              type="email"
              placeholder={t('subscribe.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="subscribe-email-input"
              className="flex-1 bg-[#2D2520] border-[#3D3531] text-[#F9F8F6] placeholder:text-[#57534E] focus:ring-[#D4AF37]"
              required
            />
            <Button type="submit" disabled={loading} data-testid="subscribe-submit-btn" className="bg-[#2A2522] hover:bg-[#1C1917] border border-[#D4AF37] text-[#D4AF37] hover:text-[#E8C5A0] rounded-full px-6">
              {t('subscribe.button')}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-[#1C1917] text-white" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <img src="https://static.prod-images.emergentagent.com/jobs/56f84a5b-135a-42ae-a69d-86265ff2961c/images/2d0056ff063a0a9085815456e6463ae1b21531676f1a55ef73e0b16a490ee653.png" alt="Colosseum Original" className="w-16 h-16 mx-auto mb-4 opacity-80 rounded-full" data-testid="footer-medallion" />
            <h4 className="text-2xl mb-2 font-light" style={{fontFamily: 'Cormorant Garamond, serif'}}>Welcome to the Colosseum&#8482;</h4>
            <p className="text-xs text-[#D4AF37]/60 italic mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>Incorporated</p>
            <p className="text-white/70">{t('footer.tagline')}</p>
          </div>
          <div className="flex justify-center mb-4">
            <LanguageSwitcher />
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-white/60 mb-4">
            <button onClick={() => navigate('/terms')} className="hover:text-[#E8C5A0] transition-colors">{t('footer.terms')}</button>
            <span>&bull;</span>
            <button onClick={() => navigate('/privacy')} className="hover:text-[#E8C5A0] transition-colors">{t('footer.privacy')}</button>
            <span>&bull;</span>
            <button onClick={() => navigate('/philosophy')} className="hover:text-[#E8C5A0] transition-colors" data-testid="footer-philosophy-link">{t('landing_extras.our_philosophy')}</button>
            <span>&bull;</span>
            <button onClick={() => navigate('/discover-binding')} className="hover:text-[#E8C5A0] transition-colors" data-testid="footer-binding-link">{t('landing_extras.discover_binding')}</button>
            <span>&bull;</span>
            <button onClick={() => navigate('/contact')} className="hover:text-[#E8C5A0] transition-colors" data-testid="footer-contact-link">{t('contact_page.header_title')}</button>
          </div>
          <div className="text-center text-xs text-white/50">
            <p>&copy; {t('footer.copyright')}</p>
            <p className="mt-2">{t('footer.manifesto_1')}</p>
            <p className="mt-1">{t('footer.manifesto_2')}</p>
            <p className="mt-1">{t('footer.manifesto_3')}</p>
            {/* Owen Feb 2026 — replay the swords-clash front-door
                gesture on demand. Useful for live partner demos
                where the intro has already faded. */}
            <button
              type="button"
              onClick={() => { try { window.dispatchEvent(new CustomEvent('replay-swords-intro')); } catch(e) { /* ignored */ } }}
              className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]/80 hover:text-[#F4EFE6] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 italic transition-colors"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="footer-replay-intro"
              aria-label="Replay the swords intro animation"
            >
              <span>&#x21bb;</span>
              <span>replay intro</span>
            </button>
          </div>
        </div>
      </footer>

      {/* Owen Feb 2026 round 35 — floating flag-only language switcher
          for MOBILE only (the header switcher handles desktop). Always
          visible at bottom-right corner so a Spanish or Italian visitor
          can flip the whole landing page into their native language
          before they ever sign up. dropUp because anchored to the
          bottom of the viewport. */}
      <div className="sm:hidden fixed bottom-4 right-4 z-40" data-testid="landing-floating-lang">
        <LanguageSwitcher dropUp />
      </div>
    </div>
  );
};

export default Landing;
